import React, { Suspense, lazy } from "react";
import { RequireAuth } from "../context/auth-context";
import { councilSlugFromHost } from "../helpers";

// Named chunks so production caches route bundles independently.
const Userlayout = lazy(() =>
  import(/* webpackChunkName: "layout-staff" */ "../layouts/user-layout"),
);
const AdminLayout = lazy(() =>
  import(/* webpackChunkName: "layout-admin" */ "../layouts/admin-layout"),
);
const PhoneLayout = lazy(() =>
  import(/* webpackChunkName: "layout-mobile" */ "../layouts/mobile"),
);
const Revenue = lazy(() =>
  import(/* webpackChunkName: "layout-revenue" */ "../layouts/revenue"),
);

const Dashboards = lazy(() =>
  import(/* webpackChunkName: "staff-dashboard" */ "../pages/staff/dashboard"),
);
const LandingPage = lazy(() =>
  import(/* webpackChunkName: "staff-budget" */ "../pages/staff"),
);
const Bills = lazy(() =>
  import(/* webpackChunkName: "staff-bills" */ "../pages/staff/bills/bills"),
);
const CashbookPage = lazy(() =>
  import(/* webpackChunkName: "staff-cashbook" */ "../pages/staff/cashbook/cashbook"),
);
const CustomerPage = lazy(() =>
  import(/* webpackChunkName: "staff-customers" */ "../pages/staff/customers/customer"),
);
const SupplierManagement = lazy(() =>
  import(/* webpackChunkName: "staff-suppliers" */ "../pages/staff/suppliers/suppliers"),
);
const Reports = lazy(() =>
  import(/* webpackChunkName: "staff-reports" */ "../pages/staff/reports"),
);
const Journal = lazy(() =>
  import(/* webpackChunkName: "staff-journal" */ "../pages/staff/journal"),
);
const Transactions = lazy(() =>
  import(/* webpackChunkName: "staff-transactions" */ "../pages/staff/cashbook/trans"),
);
const Recon = lazy(() =>
  import(/* webpackChunkName: "staff-recon" */ "../pages/staff/recon"),
);
const Loans = lazy(() =>
  import(/* webpackChunkName: "staff-loans" */ "../pages/staff/loans"),
);
const ProjectPage = lazy(() =>
  import(/* webpackChunkName: "staff-projects" */ "../pages/staff/projects"),
);
const Clients = lazy(() =>
  import(/* webpackChunkName: "staff-clients" */ "../pages/staff/clients"),
);
const Audit = lazy(() =>
  import(/* webpackChunkName: "admin-audit" */ "../pages/admin/audit"),
);

const Settings = lazy(() =>
  import(/* webpackChunkName: "admin-settings" */ "../pages/admin/settings"),
);
const Dashboard = lazy(() =>
  import(/* webpackChunkName: "admin-dashboard" */ "../pages/admin"),
);
const Users = lazy(() =>
  import(/* webpackChunkName: "admin-users" */ "../pages/admin/users"),
);
const Input = lazy(() =>
  import(/* webpackChunkName: "admin-input" */ "../pages/admin/input"),
);
const Budget = lazy(() =>
  import(/* webpackChunkName: "admin-budget" */ "../pages/admin/budget"),
);
const Opening = lazy(() =>
  import(/* webpackChunkName: "admin-opening" */ "../pages/admin/opening"),
);
const StaffProfile = lazy(() =>
  import(/* webpackChunkName: "staff-profile" */ "../pages/shared/StaffProfile"),
);

const RevenueDashboard = lazy(() =>
  import(/* webpackChunkName: "revenue-dashboard" */ "../pages/revenue/Dashboard"),
);
const LandDesk = lazy(() =>
  import(/* webpackChunkName: "revenue-land" */ "../pages/revenue/LandDesk"),
);
const Properties = lazy(() =>
  import(/* webpackChunkName: "revenue-properties" */ "../pages/revenue/Properties"),
);
const Shops = lazy(() =>
  import(/* webpackChunkName: "revenue-shops" */ "../pages/revenue/Shops"),
);
const Permits = lazy(() =>
  import(/* webpackChunkName: "revenue-permits" */ "../pages/revenue/Permits"),
);
const VerifyPermit = lazy(() =>
  import(/* webpackChunkName: "public-verify" */ "../pages/public/VerifyPermit"),
);
const VerifyDocument = lazy(() =>
  import(/* webpackChunkName: "public-verify-document" */ "../pages/public/VerifyDocument"),
);

const CouncilHome = lazy(() =>
  import(/* webpackChunkName: "public-council" */ "../pages/public/CouncilHome"),
);
const CustomerPortal = lazy(() =>
  import(/* webpackChunkName: "public-portal" */ "../pages/public/CustomerPortal"),
);

const MarketingHome = lazy(() =>
  import(/* webpackChunkName: "mkt-home" */ "../pages/marketing/Home"),
);
const MarketingAbout = lazy(() =>
  import(/* webpackChunkName: "mkt-about" */ "../pages/marketing/About"),
);
const MarketingProducts = lazy(() =>
  import(/* webpackChunkName: "mkt-products" */ "../pages/marketing/Products"),
);
const MarketingWhoWeServe = lazy(() =>
  import(/* webpackChunkName: "mkt-serve" */ "../pages/marketing/WhoWeServe"),
);
const MarketingClients = lazy(() =>
  import(/* webpackChunkName: "mkt-clients" */ "../pages/marketing/Clients"),
);
const MarketingFuture = lazy(() =>
  import(/* webpackChunkName: "mkt-future" */ "../pages/marketing/Future"),
);
const MarketingContact = lazy(() =>
  import(/* webpackChunkName: "mkt-contact" */ "../pages/marketing/Contact"),
);
const MarketingQuotation = lazy(() =>
  import(/* webpackChunkName: "mkt-quote" */ "../pages/marketing/Quotation"),
);
const PlatformConsole = lazy(() =>
  import(/* webpackChunkName: "platform" */ "../pages/platform/PlatformConsole"),
);

const councilHost = typeof window !== "undefined" && !!councilSlugFromHost();
const staffClientsPath = councilHost ? "/clients" : "/app/clients";

function LazyPage({ children }) {
  return (
    <Suspense
      fallback={
        <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500">
          Loading…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const withLazy = (element) => <LazyPage>{element}</LazyPage>;

const staffDashboard = {
  path: "/app",
  element: withLazy(<Dashboards />),
};

const apexPlatformRoute = {
  path: "/app",
  element: withLazy(
    <RequireAuth platform>
      <PlatformConsole />
    </RequireAuth>,
  ),
};

const routes = [
  { path: "/verify/d/:token", element: withLazy(<VerifyDocument />) },
  { path: "/verify/:token", element: withLazy(<VerifyPermit />) },
  ...(councilHost
    ? [
        { path: "/", element: withLazy(<CouncilHome />) },
        { path: "/customer", element: withLazy(<CustomerPortal />) },
      ]
    : [
        { path: "/", element: withLazy(<MarketingHome />) },
        { path: "/about", element: withLazy(<MarketingAbout />) },
        { path: "/products", element: withLazy(<MarketingProducts />) },
        { path: "/who-we-serve", element: withLazy(<MarketingWhoWeServe />) },
        { path: "/clients", element: withLazy(<MarketingClients />) },
        { path: "/future", element: withLazy(<MarketingFuture />) },
        { path: "/contact", element: withLazy(<MarketingContact />) },
        { path: "/quotation", element: withLazy(<MarketingQuotation />) },
        apexPlatformRoute,
      ]),
  ...(councilHost
    ? [
        {
          element: withLazy(
            <RequireAuth>
              <Userlayout />
            </RequireAuth>,
          ),
          children: [
            staffDashboard,
            { path: "/app/profile", element: withLazy(<StaffProfile />) },
            { path: "/budget", element: withLazy(<LandingPage />) },
            { path: "/bills", element: withLazy(<Bills />) },
            { path: "/cashbook", element: withLazy(<CashbookPage />) },
            { path: "/customers", element: withLazy(<CustomerPage />) },
            { path: "/suppliers", element: withLazy(<SupplierManagement />) },
            { path: "/loans", element: withLazy(<Loans />) },
            { path: "/reports", element: withLazy(<Reports />) },
            { path: "/reports/:reportName", element: withLazy(<Reports />) },
            { path: "/journal", element: withLazy(<Journal />) },
            { path: "/transactions", element: withLazy(<Transactions />) },
            { path: "/projects", element: withLazy(<ProjectPage />) },
            { path: "/recon", element: withLazy(<Recon />) },
            { path: staffClientsPath, element: withLazy(<Clients />) },
            { path: "/audit", element: withLazy(<Audit />) },
          ],
        },
      ]
    : []),
  {
    path: "/mobile",
    element: withLazy(
      <RequireAuth>
        <PhoneLayout />
      </RequireAuth>,
    ),
    children: [{ path: "/mobile", element: withLazy(<Dashboards />) }],
  },
  {
    path: "/revenue",
    element: withLazy(
      <RequireAuth>
        <Revenue />
      </RequireAuth>,
    ),
    children: [
      { path: "/revenue", element: withLazy(<RevenueDashboard />) },
      { path: "/revenue/bills", element: withLazy(<Bills />) },
      { path: "/revenue/reports", element: withLazy(<Reports />) },
      { path: "/revenue/reports/:reportName", element: withLazy(<Reports />) },
      { path: "/revenue/customers", element: withLazy(<CustomerPage />) },
      { path: "/revenue/clients", element: withLazy(<Clients />) },
      { path: "/revenue/properties", element: withLazy(<Properties />) },
      { path: "/revenue/land", element: withLazy(<LandDesk />) },
      { path: "/revenue/land-management", element: withLazy(<LandDesk />) },
      { path: "/revenue/shops", element: withLazy(<Shops />) },
      { path: "/revenue/permits", element: withLazy(<Permits />) },
    ],
  },
  {
    path: "/admin",
    element: withLazy(
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>,
    ),
    children: [
      { path: "/admin", element: withLazy(<Dashboard />) },
      { path: "/admin/profile", element: withLazy(<StaffProfile />) },
      { path: "/admin/settings", element: withLazy(<Settings />) },
      { path: "/admin/users", element: withLazy(<Users />) },
      { path: "/admin/input", element: withLazy(<Input />) },
      { path: "/admin/budget", element: withLazy(<Budget />) },
      { path: "/admin/opening", element: withLazy(<Opening />) },
    ],
  },
];

export default routes;
